import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { SettingsProvider } from "@/lib/settings";
import AnalyticsLoader from "@/components/site/AnalyticsLoader";
import ReadProgress from "@/components/site/ReadProgress";
import ErrorBoundary from "@/components/site/ErrorBoundary";

import SiteLayout from "@/components/site/SiteLayout";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Jobs from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import ResumeUpload from "@/pages/ResumeUpload";
import ResumeHealthCheck from "@/pages/ResumeHealthCheck";
import Testimonials from "@/pages/Testimonials";
import Feedback from "@/pages/Feedback";
import Contact from "@/pages/Contact";
import HireTalent from "@/pages/HireTalent";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import NotFound from "@/pages/NotFound";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminJobs from "@/pages/admin/AdminJobs";
import AdminJobEditor from "@/pages/admin/AdminJobEditor";
import AdminApplications from "@/pages/admin/AdminApplications";
import AdminPipeline from "@/pages/admin/AdminPipeline";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminFeedback from "@/pages/admin/AdminFeedback";
import AdminEnquiries from "@/pages/admin/AdminEnquiries";
import AdminHireBriefs from "@/pages/admin/AdminHireBriefs";
import AdminSubscribers from "@/pages/admin/AdminSubscribers";
import AdminPageSeo from "@/pages/admin/AdminPageSeo";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminBlogEditor from "@/pages/admin/AdminBlogEditor";
import AdminFaqs from "@/pages/admin/AdminFaqs";
import AdminDepartments from "@/pages/admin/AdminDepartments";
import AdminServices from "@/pages/admin/AdminServices";
import AdminChangePassword from "@/pages/admin/AdminChangePassword";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminClients from "@/pages/admin/AdminClients";
import AdminRecruiters from "@/pages/admin/AdminRecruiters";
import AdminInterviews from "@/pages/admin/AdminInterviews";
import AdminPlacements from "@/pages/admin/AdminPlacements";
import AdminInvoices from "@/pages/admin/AdminInvoices";
import AdminTemplates from "@/pages/admin/AdminTemplates";
import AdminTalentSearch from "@/pages/admin/AdminTalentSearch";
import AdminCandidate360 from "@/pages/admin/AdminCandidate360";
import AdminShortlists from "@/pages/admin/AdminShortlists";
import AdminKPI from "@/pages/admin/AdminKPI";
import ShortlistPortal from "@/pages/ShortlistPortal";

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <SettingsProvider>
          <AnalyticsLoader />
          <ReadProgress />
          <BrowserRouter>
        <Routes>
          <Route element={<SiteLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/jobs/:slug" element={<JobDetail />} />
            <Route path="/upload-resume" element={<ResumeUpload />} />
            <Route path="/resume-health-check" element={<ResumeHealthCheck />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/hire-talent" element={<HireTalent />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="jobs" element={<AdminJobs />} />
            <Route path="jobs/new" element={<AdminJobEditor />} />
            <Route path="jobs/:id" element={<AdminJobEditor />} />
            <Route path="applications" element={<AdminApplications />} />
            <Route path="pipeline" element={<AdminPipeline />} />
            <Route path="testimonials" element={<AdminTestimonials />} />
            <Route path="feedback" element={<AdminFeedback />} />
            <Route path="enquiries" element={<AdminEnquiries />} />
            <Route path="hire-briefs" element={<AdminHireBriefs />} />
            <Route path="subscribers" element={<AdminSubscribers />} />
            <Route path="page-seo" element={<AdminPageSeo />} />
            <Route path="blog" element={<AdminBlog />} />
            <Route path="blog/new" element={<AdminBlogEditor />} />
            <Route path="blog/:id" element={<AdminBlogEditor />} />
            <Route path="faqs" element={<AdminFaqs />} />
            <Route path="departments" element={<AdminDepartments />} />
            <Route path="service-categories" element={<AdminServices />} />
            <Route path="change-password" element={<AdminChangePassword />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="clients" element={<AdminClients />} />
            <Route path="recruiters" element={<AdminRecruiters />} />
            <Route path="interviews" element={<AdminInterviews />} />
            <Route path="placements" element={<AdminPlacements />} />
            <Route path="invoices" element={<AdminInvoices />} />
            <Route path="templates" element={<AdminTemplates />} />
            <Route path="talent-search" element={<AdminTalentSearch />} />
            <Route path="candidates/:email" element={<AdminCandidate360 />} />
            <Route path="shortlists" element={<AdminShortlists />} />
            <Route path="kpi" element={<AdminKPI />} />
          </Route>

          {/* Public token-gated client shortlist portal — no site chrome */}
          <Route path="/shortlist/:token" element={<ShortlistPortal />} />
        </Routes>
        </BrowserRouter>
        </SettingsProvider>
      </ErrorBoundary>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
